// usePeekPanelController.js

import { useCallback, useEffect, useState } from 'react';

import { useNodePeek } from 'context/NodePeekContext';
import useCatalogLiveNode from 'hooks/useCatalogLiveNode';
import useNodeDetails from 'node/hooks/useNodeDetails';
import useNodePeekData from 'node/hooks/useNodePeekData';

const usePeekPanelController = ({ user, setCurrentNode = null }) => {
    const {
        isPeekOpen,
        peekNodeKey,
        peekSeedNode,
        canPeekGoBack,
        openPeek,
        goBackPeek,
        closePeek,
    } = useNodePeek();

    const [peekLocalNode, setPeekLocalNode] = useState(null);

    const {
        peekNode,
        isLoading: isPeekLoading,
        error: peekError,
    } = useNodePeekData({
        isOpen: isPeekOpen,
        peekNodeKey,
        peekSeedNode,
    });

    // Catalog-adopted nodes: live-read title/cover/etc. from the platform catalog.
    const livePeekNode = useCatalogLiveNode(peekNode);

    const {
        handleTitleSaved: handlePeekTitleSaved,
        handleTagsSaved: handlePeekTagsSaved,
        handleLocationSaved: handlePeekLocationSaved,
        handleSubtitleSaved: handlePeekSubtitleSaved,
        handleCoverSaved: handlePeekCoverSaved,
        handleNodeChange: handlePeekNodeChange,
    } = useNodeDetails({
        node: livePeekNode,
        localNode: peekLocalNode,
        setLocalNode: setPeekLocalNode,
        setCurrentNode,
        user,
    });

    useEffect(() => {
        if (!isPeekOpen) {
            setPeekLocalNode(null);
            return;
        }

        if (!livePeekNode?.key) {
            setPeekLocalNode(null);
            return;
        }

        setPeekLocalNode((prev) => (prev === livePeekNode ? prev : livePeekNode));
    }, [isPeekOpen, livePeekNode]);

    const handlePeekBack = useCallback(() => {
        if (!canPeekGoBack) return;
        goBackPeek();
    }, [canPeekGoBack, goBackPeek]);

    return {
        isPeekOpen,
        openPeek,
        closePeek,
        peekLocalNode,
        setPeekLocalNode,
        canPeekGoBack,
        isPeekLoading,
        peekError,
        handlePeekBack,
        handlePeekTitleSaved,
        handlePeekTagsSaved,
        handlePeekLocationSaved,
        handlePeekSubtitleSaved,
        handlePeekCoverSaved,
        handlePeekNodeChange,
    };
};

export default usePeekPanelController;
